import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  merge,
  Observable,
  of,
  ReplaySubject,
  Subject,
  throwError,
} from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { SelectedSiteService } from '../../selected-site.service';
import { Job } from '../job';
import { PushLiveOptions, SiteStaging, StagingStatus } from './staging';
import { StagingApiService } from './staging.api.service';

const pushStagingJobType = 'staging-push';

@Injectable()
export class StagingService {
  templateId: string;
  siteId$: Observable<string>;
  stagingStatus$: Observable<StagingStatus>;
  stagingPush$: Observable<any>;
  stagingEnable$: Observable<any>;
  stagingReset$: Observable<any>;
  stagingAction$: Observable<any> = null;
  currentStagingStatus$: Observable<StagingStatus>;
  lastJobInfo$$: ReplaySubject<Job> = new ReplaySubject<Job>(1);

  createStagingRequest$$: Subject<null> = new Subject<null>();
  resetStagingRequest$$: Subject<null> = new Subject<null>();
  resetStagingFromTemplateRequest$$: Subject<null> = new Subject<null>();
  resetStagingFromTemplateRequest$: Observable<any>;
  pushStagingRequest$$: Subject<PushLiveOptions> = new Subject<PushLiveOptions>();
  stagingFromBackup$$: Subject<null> = new Subject<null>();
  stagingFromBackup$: Observable<any> = this.stagingFromBackup$$.asObservable();
  private errors$$: BehaviorSubject<HttpErrorResponse | null> = new BehaviorSubject<HttpErrorResponse | null>(null);

  constructor(private api: StagingApiService, private selectedSiteService: SelectedSiteService) {
    this.siteId$ = this.selectedSiteService.getSiteId();

    this.stagingPush$ = this.pushStagingRequest$$.pipe(
      withLatestFrom(this.siteId$),
      switchMap(([req, siteId]: [PushLiveOptions, string]) => {
        return this.api.httpPostPushStaging(req, siteId).pipe(
          catchError((err) => {
            this.errors$$.next(err);
            return of();
          }),
        );
      }),
      tap(() => this.errors$$.next(null)), // clear errors on a successful push staging request
      startWith(null),
    );

    this.stagingEnable$ = this.createStagingRequest$$.pipe(
      withLatestFrom(this.siteId$),
      switchMap(([, siteId]: [null, string]) => {
        return this.api.httpPostCreateStaging(siteId).pipe(
          catchError((err) => {
            this.errors$$.next(err);
            return of();
          }),
        );
      }),
      tap(() => this.errors$$.next(null)), // clear errors on a successful create staging request
    );

    this.stagingReset$ = this.resetStagingRequest$$.pipe(
      withLatestFrom(this.siteId$),
      switchMap(([, siteId]: [null, string]) => {
        return this.api.httpPostResetStaging(siteId).pipe(
          catchError((err) => {
            this.errors$$.next(err);
            return of();
          }),
        );
      }),
      tap(() => this.errors$$.next(null)), // clear errors on a successful reset staging request
    );

    this.resetStagingFromTemplateRequest$ = this.resetStagingFromTemplateRequest$$.pipe(
      withLatestFrom(this.siteId$),
      switchMap(([, siteId]: [null, string]) => {
        return this.api.httpPostResetStagingFromTemplate(siteId, this.templateId).pipe(
          catchError((err) => {
            this.errors$$.next(err);
            return of();
          }),
        );
      }),
      tap(() => this.errors$$.next(null)), // clear errors on a successful reset staging request
    );

    this.stagingAction$ = merge(
      this.stagingEnable$,
      this.stagingReset$,
      this.stagingFromBackup$,
      this.resetStagingFromTemplateRequest$,
    ).pipe(startWith(null));

    this.lookupStagingStatus();

    this.stagingStatus$ = combineLatest([this.stagingPush$, this.stagingAction$]).pipe(
      switchMap(() => this.currentStagingStatus$),
    );
  }

  public lookupStagingStatus(): void {
    this.currentStagingStatus$ = combineLatest([this.lookupJobs(pushStagingJobType), this.getSiteStagingStatus()]).pipe(
      switchMap(([job, status]: [Job, StagingStatus]) => {
        if (job && job.status === Job.IN_PROGRESS) {
          return this.pollForJobCompletion(pushStagingJobType);
        }
        if (status === SiteStaging.CREATING || status === SiteStaging.PUBLISHING) {
          return this.siteStagingStatusChangesTimer();
        }
        return of(status);
      }),
      catchError((err) => throwError(err)),
    );
  }

  getSiteStagingStatus(): Observable<StagingStatus> {
    return this.siteId$.pipe(
      switchMap((siteId: string) =>
        this.api.httpGetStagingStatus(siteId).pipe(
          catchError((err) => {
            this.errors$$.next(err);
            return throwError(err);
          }),
        ),
      ),
      tap(() => this.errors$$.next(null)), // clear errors on a successful get staging status request
    );
  }

  getStagingStatus(): Observable<StagingStatus> {
    return this.stagingStatus$;
  }

  getLastJobInfo(): Observable<any> {
    return this.lastJobInfo$$.asObservable();
  }

  createStagingRequest(): void {
    this.createStagingRequest$$.next(null);
  }

  resetStagingRequest(): void {
    this.resetStagingRequest$$.next(null);
  }

  resetStagingFromTemplateRequest(templateId: string): void {
    this.templateId = templateId;
    this.resetStagingFromTemplateRequest$$.next(null);
  }

  stagingFromBackup(): void {
    this.stagingFromBackup$$.next(null);
  }

  pushStagingRequest(options: PushLiveOptions): void {
    this.pushStagingRequest$$.next(options);
  }

  getErrors(): Observable<HttpErrorResponse | null> {
    return this.errors$$.asObservable();
  }

  private lookupJobs(jobType: string): Observable<Job> {
    return this.siteId$.pipe(
      switchMap((siteId: string) => {
        return this.api.httpLookupLatestJob(siteId, jobType, 1).pipe(
          catchError((err) => {
            this.errors$$.next(err);
            return throwError(err);
          }),
        );
      }),
      tap((job: Job) => {
        if (job) {
          this.lastJobInfo$$.next(job);
        }
        this.errors$$.next(null); // clear errors on a successful lookup job request
      }),
    );
  }

  /**
   * Set up a timer to poll every 5 seconds to check if the staging work is completed
   *  The first time it polls, it should receive a CREATING status
   *  That will be the first item in the take()
   *  Keep polling, but it will not pass the distinctUntilChanged() until it receives ON or OFF
   *  Take that and stop polling (it will be the second item in the take())
   */
  private siteStagingStatusChangesTimer(): Observable<StagingStatus> {
    return interval(5000).pipe(
      switchMap(() => this.getSiteStagingStatus()),
      distinctUntilChanged(),
      take(2),
    );
  }

  private pollForJobCompletion(jobType: string): Observable<StagingStatus> {
    return interval(5000).pipe(
      switchMap(() => this.lookupJobs(jobType)),
      map((job: Job) => {
        if (job.status === Job.IN_PROGRESS) {
          return 'publishing' as StagingStatus;
        } else {
          return 'finished' as StagingStatus; // Show users the 'finished' screen
        }
      }),
      distinctUntilChanged(),
      take(2),
    );
  }
}
